import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Users, CheckCircle, XCircle, AlertTriangle, FileText, Calendar, User } from 'lucide-react';
import { format } from 'date-fns';

export default function DuplicateReviews({ user }) {
  const [pendingReviews, setPendingReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [selectedScores, setSelectedScores] = useState({});

  useEffect(() => {
    fetchPendingReviews();
  }, []);

  const fetchPendingReviews = async () => {
    try {
      const response = await axios.get(`${API}/admin/pending-duplicates`);
      setPendingReviews(response.data);
      
      // Initialize selected scores (default to first score for each duplicate)
      const initial = {};
      response.data.forEach(review => {
        initial[review.id] = {};
        review.duplicates.forEach(dup => {
          const key = `${dup.name}|${dup.division}`;
          initial[review.id][key] = [0]; // Default to first score
        });
      });
      setSelectedScores(initial);
    } catch (error) {
      toast.error('Failed to load pending reviews');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const toggleScoreSelection = (reviewId, name, division, scoreIndex) => {
    const key = `${name}|${division}`;
    setSelectedScores(prev => {
      const current = prev[reviewId]?.[key] || [];
      let updated;
      
      if (current.includes(scoreIndex)) {
        // Remove if already selected (but keep at least one)
        updated = current.filter(i => i !== scoreIndex);
        if (updated.length === 0) updated = [scoreIndex]; // Keep at least one
      } else {
        // Add to selection
        updated = [...current, scoreIndex];
      }
      
      return {
        ...prev,
        [reviewId]: {
          ...prev[reviewId],
          [key]: updated
        }
      };
    });
  };

  const handleResolve = async (reviewId, review) => {
    setProcessingId(reviewId);
    
    try {
      // Build resolution payload
      const resolutions = review.duplicates.map(dup => {
        const key = `${dup.name}|${dup.division}`;
        const indices = selectedScores[reviewId]?.[key] || [0];
        return {
          name: dup.name,
          division: dup.division,
          selected_indices: indices
        };
      });
      
      await axios.post(`${API}/admin/pending-duplicates/${reviewId}/resolve`, {
        resolutions
      });
      
      toast.success('Duplicates resolved and scorecard created!');
      fetchPendingReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resolve duplicates');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (reviewId) => {
    if (!window.confirm('Are you sure you want to reject this upload? The scorecard will not be created.')) {
      return;
    }
    
    setProcessingId(reviewId);
    try {
      await axios.post(`${API}/admin/pending-duplicates/${reviewId}/reject`);
      toast.success('Upload rejected');
      fetchPendingReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center min-h-screen">
          <p className="font-barlow text-xl text-slate-900">Loading...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="max-w-7xl mx-auto p-4 md:p-8 lg:p-12">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-8 h-8 text-amber-600" strokeWidth={1.5} />
            <h1 className="font-barlow font-bold text-4xl md:text-5xl text-slate-900 uppercase tracking-tight">
              Duplicate Reviews
            </h1>
          </div>
          <p className="font-inter text-stone-600">
            Review and resolve scorecards with duplicate player entries
          </p>
        </div>

        {/* Info Box */}
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
            <div className="font-inter text-sm text-amber-800">
              <p className="font-semibold mb-1">How to resolve duplicates:</p>
              <ul className="list-disc list-inside space-y-1 text-amber-700">
                <li>Click on scores to select which one(s) to keep</li>
                <li>You can select multiple scores if the player competed multiple times legitimately</li>
                <li>Click "Resolve" to create the scorecard with your selections</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Pending Reviews */}
        {pendingReviews.length === 0 ? (
          <div className="bg-white border border-stone-200 rounded-sm p-12 text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" strokeWidth={1.5} />
            <p className="font-barlow font-bold text-xl text-slate-900 mb-2">All Clear!</p>
            <p className="font-inter text-stone-500">No pending duplicate reviews</p>
          </div>
        ) : (
          <div className="space-y-6">
            {pendingReviews.map((review) => (
              <div 
                key={review.id} 
                className="bg-white border border-stone-200 rounded-sm shadow-sm overflow-hidden"
                data-testid={`duplicate-review-${review.id}`}
              >
                {/* Review Header */}
                <div className="bg-stone-50 border-b border-stone-200 p-4">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-amber-600" strokeWidth={1.5} />
                      <h3 className="font-barlow font-bold text-lg text-slate-900">{review.file_name}</h3>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-sm font-inter text-stone-600">
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" strokeWidth={1.5} />
                        {review.uploader_name}
                      </span>
                      {review.scorecard_date && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" strokeWidth={1.5} />
                          Match: {review.scorecard_date}
                        </span>
                      )}
                      <span className="text-stone-400">
                        Uploaded: {format(new Date(review.created_at), 'MMM dd, yyyy HH:mm')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Duplicates Section */}
                <div className="p-6">
                  <h4 className="font-barlow font-bold text-sm uppercase tracking-wider text-amber-600 mb-4">
                    Duplicate Players ({review.duplicates.length})
                  </h4>
                  
                  <div className="space-y-4">
                    {review.duplicates.map((dup, dupIdx) => {
                      const key = `${dup.name}|${dup.division}`;
                      const selected = selectedScores[review.id]?.[key] || [0];
                      
                      return (
                        <div key={dupIdx} className="bg-amber-50 border border-amber-200 rounded-sm p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="font-barlow font-bold text-slate-900">{dup.name}</span>
                            <span className="bg-amber-600 text-white text-xs px-2 py-0.5 rounded font-barlow uppercase">
                              {dup.division}
                            </span>
                            <span className="text-amber-700 text-sm font-inter">
                              — {dup.scores.length} entries found
                            </span>
                          </div>
                          
                          <div className="flex flex-wrap gap-2">
                            {dup.scores.map((score, scoreIdx) => (
                              <button
                                key={scoreIdx}
                                onClick={() => toggleScoreSelection(review.id, dup.name, dup.division, scoreIdx)}
                                data-testid={`score-btn-${review.id}-${dupIdx}-${scoreIdx}`}
                                className={`px-4 py-2 rounded-sm font-mono text-lg font-bold transition-all ${
                                  selected.includes(scoreIdx)
                                    ? 'bg-green-600 text-white ring-2 ring-green-400'
                                    : 'bg-white text-slate-700 border border-stone-300 hover:border-amber-400'
                                }`}
                              >
                                {score}
                                {selected.includes(scoreIdx) && (
                                  <CheckCircle className="w-4 h-4 inline ml-2" strokeWidth={2} />
                                )}
                              </button>
                            ))}
                          </div>
                          
                          <p className="text-xs text-amber-700 mt-2 font-inter">
                            Selected: {selected.map(i => dup.scores[i]).join(', ')}
                          </p>
                        </div>
                      );
                    })}
                  </div>

                  {/* Non-duplicate players info */}
                  {review.non_duplicate_players.length > 0 && (
                    <div className="mt-6 pt-4 border-t border-stone-200">
                      <h4 className="font-barlow font-bold text-sm uppercase tracking-wider text-stone-500 mb-2">
                        Other Players ({review.non_duplicate_players.length})
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {review.non_duplicate_players.slice(0, 10).map((player) => (
                          <span key={`${player.name}-${player.division}`} className="bg-stone-100 text-stone-700 text-xs px-2 py-1 rounded font-inter">
                            {player.name} ({player.division}: {player.score})
                          </span>
                        ))}
                        {review.non_duplicate_players.length > 10 && (
                          <span className="text-stone-500 text-xs px-2 py-1 font-inter">
                            +{review.non_duplicate_players.length - 10} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="bg-stone-50 border-t border-stone-200 p-4 flex justify-end gap-3">
                  <Button
                    onClick={() => handleReject(review.id)}
                    disabled={processingId === review.id}
                    variant="outline"
                    data-testid={`reject-btn-${review.id}`}
                    className="border-red-300 text-red-600 hover:bg-red-50 rounded-sm font-barlow font-semibold uppercase tracking-wide"
                  >
                    <XCircle className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Reject Upload
                  </Button>
                  <Button
                    onClick={() => handleResolve(review.id, review)}
                    disabled={processingId === review.id}
                    data-testid={`resolve-btn-${review.id}`}
                    className="bg-green-600 text-white hover:bg-green-700 rounded-sm font-barlow font-semibold uppercase tracking-wide"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    {processingId === review.id ? 'Processing...' : 'Resolve & Create Scorecard'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
